// External libraries
import React, { useEffect } from 'react'; // eslint-disable-line no-unused-vars
import { useStoreState } from '@/store';

// Internal modules
import { features, getContent } from '@/lib';
import { PaypalThemedProvider } from '@/styles';
import { useMountLogging } from '@/lib/reactHooks';
import { SwitchComponent } from './StyledComponents';
import { SignupFields } from '@/components/SignupFields';
//@ts-expect-error untyped import
import { changeHandler } from '@/components/Form/handlers';
import { incrementCountToggle, toggleOnboardOption } from './utils';
// @ts-expect-error untyped imports
import { CheckoutIncentives } from '@/components/CheckoutIncentives';
import { ExpandedValueProps } from '@/components/CheckoutIncentives/ExpandedValueProps';
// @ts-expect-error untyped imports
import { StreamlineSectionHeader } from '@/components/Form/Components';
import { logFPTIInitialToggleState, logToggleSignupButton } from './fpti';
import { StreamlineLegalDisclosures } from '@/components/SignupFields/LegalDisclosures/StreamlineLegalDisclosures';
import { LazyKanaAndKanjiNames } from '@/components/SignupFields/Components';
import { verifyAndResetFullNames } from '@/components/SignupFields/KanaAndKanjiNames/utils';

const handleToggleOptions = (isSignup: boolean) => {
  if (isSignup) {
    toggleOnboardOption('guest');
  } else {
    toggleOnboardOption('signup');
  }

  logToggleSignupButton(!isSignup);
};

interface StreamlineOnboardOptionsProps {
  isPasswordless?: boolean;
}

const StreamlineOnboardOptions = ({
  isPasswordless,
}: StreamlineOnboardOptionsProps): JSX.Element => {
  useMountLogging('StreamlinedOnboardOptions');
  const { countToggles, isSignupIncentiveOptIn, value } = useStoreState(
    ({ form, ui }) => {
      return {
        countToggles: ui.countToggles,
        isSignupIncentiveOptIn: form.isSignupIncentiveOptIn,
        value: form.onboardOption?.value,
      };
    },
  );

  const isGuest = value === 'guest';
  const isSignup = value === 'signup';

  // Expanded Value Props.
  const expandedValueProps = features.isActive('isExpandedValueProps');
  // In treatment, but xobuyer flagged ineligible.
  const ineligibleStretch = features.isActive(
    'isExpandedStretchRewardsIneligibleDownstream',
  );

  // log to fpti the initial value of the toggle
  useEffect(() => {
    logFPTIInitialToggleState(isSignup);
  }, []);

  const handleSwitchOnChange = (isSignup: boolean) => {
    handleToggleOptions(isSignup);
    incrementCountToggle(countToggles);
    verifyAndResetFullNames();
  };

  const toggleComponent = (
    <PaypalThemedProvider>
      <SwitchComponent
        checked={isSignup}
        data-testid="onboard-options-switch"
        // Expanded Purchase Protection uses its own label not attached to OnboardSwitch
        hideLabel={expandedValueProps && !ineligibleStretch}
        inline
        label={getContent('simplifiedSectionHeaders.signup.toggle')}
        onChange={() => handleSwitchOnChange(isSignup)}
        value={value}
      />
    </PaypalThemedProvider>
  );

  const hasExpandedValueProps =
    toggleComponent && !isSignupIncentiveOptIn && expandedValueProps;

  return (
    <>
      {/* Show Expanded Value props if in treatment, but pass along the toggle.
          Otherwise, just show the toggle. Used in both scenarios.
          We check stretch eligibility further down. */}
      {hasExpandedValueProps && !ineligibleStretch ? (
        <ExpandedValueProps toggleComponent={toggleComponent} />
      ) : (
        toggleComponent
      )}
      {/* This line makes sure we show incentives only when toggle is
          shown and not in expanded value props */}
      {!expandedValueProps && <CheckoutIncentives />}
      {isSignup && (
        <>
          {!isSignupIncentiveOptIn && (
            <StreamlineSectionHeader showHeader={false} />
          )}
          <SignupFields
            isPasswordless={isPasswordless}
            isStreamlineExperience
          />
        </>
      )}
      {isGuest && (
        <>
          <StreamlineLegalDisclosures
            changeHandler={changeHandler}
            type="guest"
          />
          {features.isActive('shouldDisplayJpKanaAndKanjiNames') && (
            <LazyKanaAndKanjiNames />
          )}
        </>
      )}
    </>
  );
};

export { StreamlineOnboardOptions };
